import { LayoutGroup, motion, useReducedMotion } from 'framer-motion'
import { useEffect, useId, useRef, useState } from 'react'

import {
  mergeReducedMotionPreset,
  resolveTabIndicatorTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import type { ThemeId } from '../../theme/ThemeContext'
import { useTheme } from '../../theme/ThemeContext'
import { THEME_SEGMENT_ICONS } from './themeSegmentIcons'

export type ThemeSegmentRailProps = {
  /** 顶栏：更小按钮，单行（见 nowrap） */
  compact?: boolean
  /** compact 时强制一行（PC 顶栏） */
  nowrap?: boolean
  /** 非 compact 网格：展示简称（弹层/设置页触控友好） */
  showOptionLabels?: boolean
  /** 选中主题后回调（如关闭弹层） */
  onThemeSelect?: (id: ThemeId) => void
}

export function ThemeSegmentRail({
  compact,
  showOptionLabels,
  onThemeSelect,
}: ThemeSegmentRailProps) {
  const { theme, setTheme, themeOptions } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const preset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const pillTrans = resolveTabIndicatorTransition(
    mergeReducedMotionPreset(preset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )
  const layoutScope = useId().replaceAll(':', '')
  const prevThemeRef = useRef(theme)
  const [iconTransitionFrom, setIconTransitionFrom] = useState<ThemeId | null>(null)

  useEffect(() => {
    if (prevThemeRef.current === theme) return
    const from = prevThemeRef.current
    prevThemeRef.current = theme
    if (reducedMotion) {
      setIconTransitionFrom(null)
      return
    }
    setIconTransitionFrom(from)
    const timer = window.setTimeout(() => {
      setIconTransitionFrom((curr) => (curr === from ? null : curr))
    }, 320)
    return () => window.clearTimeout(timer)
  }, [theme, reducedMotion])

  const useSlidePill = !(compact && angular)

  const pickTheme = (id: ThemeId) => {
    setTheme(id)
    onThemeSelect?.(id)
  }

  const btnClass = (active: boolean) => {
    if (compact && angular) {
      return [
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-none',
        'transition-colors',
        active
          ? 'bg-[var(--segment-active-bg)] text-[var(--segment-active-fg)]'
          : 'text-ink-soft hover:bg-surface-muted hover:text-ink',
      ].join(' ')
    }
    if (compact) {
      return [
        'relative inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-[10px]',
        active
          ? 'text-[var(--segment-active-fg)]'
          : 'text-ink-soft hover:bg-menu-solid hover:text-ink',
      ].join(' ')
    }
    if (showOptionLabels) {
      return [
        'relative flex w-full min-w-0 flex-col items-center justify-center gap-1 overflow-hidden rounded-[10px] px-1 py-2.5',
        active
          ? 'text-[var(--segment-active-fg)]'
          : 'text-ink-soft hover:bg-menu-solid hover:text-ink',
      ].join(' ')
    }
    return [
      'relative inline-flex h-10 w-10 items-center justify-center overflow-hidden rounded-[10px]',
      active
        ? 'text-[var(--segment-active-fg)]'
        : 'text-ink-soft hover:bg-menu-solid hover:text-ink',
    ].join(' ')
  }

  const iconWrapClass = compact
    ? 'relative z-10 inline-flex h-4 w-4 shrink-0 items-center justify-center'
    : 'relative z-10 inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center'
  const iconSvgClass = 'h-full w-full'

  const tooltipClass = [
    compact
      ? 'pointer-events-none absolute top-full left-1/2 z-[60] mt-1.5 -translate-x-1/2'
      : 'pointer-events-none absolute bottom-full left-1/2 z-[60] mb-1.5 -translate-x-1/2',
    'whitespace-nowrap border border-line bg-panel px-2 py-1 text-[11px] font-medium text-ink shadow-card',
    'invisible opacity-0 transition-[opacity,visibility] duration-150',
    'group-hover:visible group-hover:opacity-100',
    angular ? 'rounded-none' : 'rounded-md',
  ].join(' ')

  const railWrap = (() => {
    if (compact && angular) {
      return [
        'theme-segment-compact-rail inline-flex max-h-9 min-h-0 shrink-0 flex-nowrap items-center',
        'rounded-none border-2 border-line bg-menu-solid shadow-none',
      ].join(' ')
    }
    if (compact) {
      return [
        'inline-flex w-max max-w-none justify-end gap-0.5 rounded-[var(--radius-sm)] border border-line bg-menu-muted p-0.5',
        'flex-nowrap',
      ].join(' ')
    }
    if (showOptionLabels) {
      return 'grid grid-cols-2 gap-1.5 rounded-[var(--radius-sm)] border border-line bg-menu-muted p-1.5 sm:grid-cols-4'
    }
    return 'grid grid-cols-4 gap-1 rounded-[var(--radius-sm)] border border-line bg-menu-muted p-1'
  })()

  return (
    <LayoutGroup id={`theme-seg-${layoutScope}`}>
      <div className={railWrap} role="group" aria-label="外观主题">
        {themeOptions.map((t) => {
          const Icon = THEME_SEGMENT_ICONS[t.id]
          const active = theme === t.id
          const showOverlay = active && !!iconTransitionFrom && !reducedMotion
          const PrevIcon =
            showOverlay && iconTransitionFrom
              ? THEME_SEGMENT_ICONS[iconTransitionFrom]
              : null
          return (
            <div
              key={t.id}
              className={showOptionLabels ? 'group relative min-w-0' : 'group relative shrink-0'}
            >
              <button
                type="button"
                aria-label={`主题：${t.label}`}
                aria-pressed={active}
                onClick={() => pickTheme(t.id)}
                className={btnClass(active)}
              >
                {useSlidePill && active ? (
                  <motion.span
                    layoutId={`theme-seg-pill-${layoutScope}`}
                    className="pointer-events-none absolute inset-0 z-0 rounded-[10px] bg-[var(--segment-active-bg)] shadow-sm ring-1 ring-[var(--segment-active-ring)]"
                    transition={pillTrans}
                  />
                ) : null}
                <span className={iconWrapClass}>
                  {showOverlay && PrevIcon ? (
                    <>
                      <motion.span
                        className="absolute inset-0 inline-flex items-center justify-center"
                        initial={{ opacity: 1, scale: 1 }}
                        animate={{ opacity: 0, scale: 0.92 }}
                        transition={{
                          duration: 0.24,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                      >
                        <PrevIcon className={iconSvgClass} />
                      </motion.span>
                      <motion.span
                        className="absolute inset-0 inline-flex items-center justify-center"
                        initial={{ opacity: 0, scale: 1.08 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.26,
                          ease: [0.22, 1, 0.36, 1],
                        }}
                      >
                        <Icon className={iconSvgClass} />
                      </motion.span>
                    </>
                  ) : (
                    <Icon className={iconSvgClass} />
                  )}
                </span>
                {showOptionLabels ? (
                  <span className="relative z-10 text-[11px] font-medium leading-tight">{t.label}</span>
                ) : (
                  <span className="sr-only">{t.label}</span>
                )}
              </button>
              {!(compact && angular) && !showOptionLabels ? (
                <span role="tooltip" className={tooltipClass}>
                  {t.label}
                </span>
              ) : null}
            </div>
          )
        })}
      </div>
    </LayoutGroup>
  )
}
